import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import {
    PaymentMethodSelector,
    IPType,
    isPaymentIntentResult,
    type ResolvePaymentClientSecretParams,
} from '../../components/Checkout/PaymentMethodSelector';
import { useStripePromise } from '../../hook/useStripePromise';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { getJWtDetails, showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { fundWallet, getWalletDashboard } from '../../redux/buzzbridge/actions';
import { IPaymentCard } from '../../types/global/generic.types';
import { useDispatch } from 'react-redux';
import { IFundWalletResponse, IWalletFundOptions, IWalletFundPreset } from '../../redux/buzzbridge/types';
import {
    buildWalletFeeCalcPayload,
    chargesInPaymentCurrency,
    formatApiAmountLabel,
    formatChargeAmount,
    formatChargeFromOptions,
    formatUsdWalletCents,
    getMinimumEnterAmountPrompt,
    getMinimumSelectPresetPrompt,
    getEffectiveMinWalletTopUpCents,
    getPaymentCurrencySymbol,
    getPresetFeesFormatted,
    getPresetProcessingFeesCents,
    getPresetTaxCents,
    getYouPayAmountCents,
    formatYouPayDisplay,
    isAdminTestPreset,
    isWalletTopUpAmountAllowed,
} from './walletFundOptionsUtils';
import {
    buildWalletFundStripeDispatchPayload,
    buildWalletStripeElementsOptions,
    isStripeFundResponse,
} from './walletFundRailUtils';
import { WalletFundingSummary } from './WalletFundingSummary';
import {
    grossChargeCentsFromSalesCalc,
    resolveWalletCreditUsdCents,
    salesCalcPreviewIsValid,
    type ICampaignWalletSalesCalc,
    unwrapSalesCalcBody,
} from './walletSalesCalcUtils';
import { api } from '../../configurations/api';
import { calculateFeesUrl } from '../../configurations/api/url';
import { httpRequest } from '../../redux/types';

const FEE_PREVIEW_DEBOUNCE_MS = 350;

interface WalletPaymentSectionProps {
    amountCents: number;
    /** When user types a custom card-charge amount (local currency), pass gross cents here. */
    customPaymentChargeCents?: number | null;
    onPaymentComplete: () => void;
    fundOptions?: IWalletFundOptions | null;
    /** User-clicked preset only — used for sales-calc when it matches amountCents */
    selectedPreset?: IWalletFundPreset | null;
    /** Resolved preset (selected or matched) — uses API you_pay + fund_request when funding */
    activePreset?: IWalletFundPreset | null;
}

function WalletPresetSummary({
    preset,
    walletCreditUsdCents,
    chargesInLocal,
    fundOptions,
    t,
}: {
    preset: IWalletFundPreset;
    walletCreditUsdCents: number;
    chargesInLocal: boolean;
    fundOptions: IWalletFundOptions | null;
    t: (typeof theme)['dark'];
}) {
    const youPay = formatYouPayDisplay(preset, fundOptions);
    const paySymbol = preset.you_pay?.symbol ?? getPaymentCurrencySymbol(fundOptions, preset);
    const feesCents = isAdminTestPreset(preset) ? 0 : getPresetProcessingFeesCents(preset);
    const feesLabel = getPresetFeesFormatted(preset, fundOptions);
    const taxCents = isAdminTestPreset(preset) ? 0 : getPresetTaxCents(preset);
    const fmtCharge = (cents: number) =>
        chargesInLocal ? formatChargeAmount(cents, paySymbol) : formatUsdWalletCents(cents);
    const localEquiv =
        formatApiAmountLabel(preset.wallet_credit_local_equivalent?.formatted, fundOptions, preset) ??
        formatApiAmountLabel(preset.conversion?.local_wallet_equivalent_formatted, fundOptions, preset);

    return (
        <Box
            sx={{
                mt: 2,
                mb: 1,
                p: 2,
                borderRadius: 2,
                bgcolor: t.secondaryBg,
                border: '1px solid ' + t.selectableBorder,
            }}>
            <Typography
                sx={{
                    fontSize: 10,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: t.form_label,
                    mb: 1.5,
                }}>
                Funding summary
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontSize: 13, color: t.form_label }}>Wallet credit (USD)</Typography>
                    <Typography sx={{ fontSize: 13, fontFamily: FONT_ENUM.MAIN_BOLD, color: t.primary_white }}>
                        {formatUsdWalletCents(walletCreditUsdCents)}
                    </Typography>
                </Box>
                {localEquiv && chargesInLocal && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: t.form_label }}>Indicative local equivalent</Typography>
                        <Typography sx={{ fontSize: 13, color: t.primary_white }}>{localEquiv}</Typography>
                    </Box>
                )}
                {feesCents > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: t.form_label }}>Fees and processing</Typography>
                        <Typography sx={{ fontSize: 13, color: t.primary_white }}>
                            {feesLabel ?? fmtCharge(feesCents)}
                        </Typography>
                    </Box>
                )}
                {taxCents > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: t.form_label }}>Tax</Typography>
                        <Typography sx={{ fontSize: 13, color: t.primary_white }}>{fmtCharge(taxCents)}</Typography>
                    </Box>
                )}
                <Box
                    sx={{
                        pt: 1,
                        mt: 0.5,
                        borderTop: '1px solid ' + t.selectableBorder,
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                        alignItems: 'baseline',
                    }}>
                    <Typography
                        sx={{ fontSize: 14, fontFamily: FONT_ENUM.MAIN_BOLD, color: t.primary_white, flexShrink: 0 }}>
                        You will pay
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 18,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: t.primary_orange,
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                        }}>
                        {youPay}
                    </Typography>
                </Box>
            </Box>
            <Typography sx={{ fontSize: 11, color: t.form_label, mt: 1.5, lineHeight: 1.45 }}>
                {chargesInLocal
                    ? `Your card is charged in ${
                          fundOptions?.payment_currency_upper ??
                          fundOptions?.payment_currency?.toUpperCase() ??
                          'local currency'
                      }; your campaign wallet is credited in USD after payment succeeds.`
                    : 'Card is charged the total above; your wallet balance increases by the net USD amount after payment succeeds.'}
            </Typography>
        </Box>
    );
}

/**
 * Outer wrapper — fee preview via sales-calc, then <Elements> with gross amount aligned to Stripe PaymentIntent.
 */
export default function WalletPaymentSection({
    amountCents,
    customPaymentChargeCents = null,
    onPaymentComplete,
    fundOptions = null,
    selectedPreset = null,
    activePreset = null,
}: WalletPaymentSectionProps) {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const stripePromise = useStripePromise();
    const [saveForFuture, setSaveForFuture] = useState(false);
    const [feePreview, setFeePreview] = useState<ICampaignWalletSalesCalc | null>(null);
    const [feeLoading, setFeeLoading] = useState(false);
    const [feeFetchKey, setFeeFetchKey] = useState(0);
    const [feeRequestDone, setFeeRequestDone] = useState(false);

    const paymentCurrency = (
        activePreset?.fund_request.currency ??
        fundOptions?.payment_currency ??
        'usd'
    ).toLowerCase();
    const minAmountCents = getEffectiveMinWalletTopUpCents(fundOptions, selectedPreset);
    const chargesInLocal = chargesInPaymentCurrency(fundOptions);

    const hasValidAmount = isWalletTopUpAmountAllowed(
        amountCents,
        fundOptions,
        selectedPreset,
        customPaymentChargeCents,
    );
    const netForRequest = Math.max(amountCents, 100);
    const presetGrossCents = activePreset ? getYouPayAmountCents(activePreset) : null;
    const hasPresetPaymentFallback = Boolean(activePreset && presetGrossCents && presetGrossCents >= 100);

    useEffect(() => {
        if (!hasValidAmount || netForRequest < 100) {
            setFeePreview(null);
            setFeeLoading(false);
            setFeeRequestDone(false);
            return;
        }

        setFeeRequestDone(false);

        let cancelled = false;
        const timer = window.setTimeout(() => {
            setFeeLoading(true);
            setFeePreview(null);
            api(
                calculateFeesUrl,
                httpRequest.POST,
                buildWalletFeeCalcPayload(netForRequest, fundOptions, {
                    selectedPreset,
                    paymentChargeCents: customPaymentChargeCents ?? undefined,
                }),
                0,
                0,
            )
                .then((res: unknown) => {
                    if (cancelled) return;
                    const data = unwrapSalesCalcBody(res);
                    setFeePreview(data);
                })
                .catch(() => {
                    if (cancelled) return;
                    setFeePreview(null);
                    showToastMessage('Could not load fee preview. Try again or adjust the amount.', 'error');
                })
                .finally(() => {
                    if (!cancelled) {
                        setFeeLoading(false);
                        setFeeRequestDone(true);
                    }
                });
        }, FEE_PREVIEW_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [
        amountCents,
        netForRequest,
        hasValidAmount,
        feeFetchKey,
        fundOptions,
        selectedPreset,
        activePreset,
        customPaymentChargeCents,
    ]);

    const grossCents = grossChargeCentsFromSalesCalc(feePreview) ?? presetGrossCents;
    const previewMatchesNet =
        (feePreview != null &&
            salesCalcPreviewIsValid(
                feePreview,
                netForRequest,
                fundOptions,
                customPaymentChargeCents,
                selectedPreset,
            )) ||
        (hasPresetPaymentFallback && feeRequestDone && !feePreview && !feeLoading);

    const elementsAmount = useMemo(() => {
        if (hasValidAmount && grossCents && grossCents >= 100) return grossCents;
        return Math.max(amountCents, 100);
    }, [hasValidAmount, grossCents, amountCents]);

    const options: StripeElementsOptions = useMemo(
        () =>
            buildWalletStripeElementsOptions({
                amount: elementsAmount,
                currency: paymentCurrency,
                saveCard: saveForFuture,
                appearance: { theme: themeStyle === 'dark' ? 'night' : 'flat' },
            }),
        [elementsAmount, themeStyle, paymentCurrency, saveForFuture],
    );

    const retryFees = useCallback(() => {
        setFeeFetchKey(k => k + 1);
    }, []);

    if (!stripePromise) return null;

    return (
        <Box sx={{ mt: 3 }}>
            {hasValidAmount && feeLoading && !grossCents && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                    <CircularProgress size={20} sx={{ color: t.primary_orange }} />
                    <Typography sx={{ fontSize: 13, color: t.form_label }}>Calculating fees…</Typography>
                </Box>
            )}
            {hasValidAmount && feeRequestDone && !feeLoading && !grossCents && (
                <Box sx={{ py: 2 }}>
                    <Typography sx={{ fontSize: 13, color: t.form_label, mb: 1 }}>
                        Fee preview unavailable. Check your connection and try again.
                    </Typography>
                    <Button size="small" onClick={retryFees} sx={{ textTransform: 'none' }}>
                        Retry
                    </Button>
                </Box>
            )}
            {hasValidAmount && feePreview && previewMatchesNet && (
                <WalletFundingSummary
                    fee={feePreview}
                    fallbackWalletCreditUsdCents={netForRequest}
                    fundOptions={fundOptions}
                    selectedPreset={selectedPreset}
                    activePreset={activePreset}
                    chargesInLocal={chargesInLocal}
                    themeStyle={themeStyle}
                />
            )}
            {hasValidAmount &&
                feeRequestDone &&
                !feePreview &&
                !feeLoading &&
                hasPresetPaymentFallback &&
                activePreset && (
                    <WalletPresetSummary
                        preset={activePreset}
                        walletCreditUsdCents={netForRequest}
                        chargesInLocal={chargesInLocal}
                        fundOptions={fundOptions}
                        t={t}
                    />
                )}
            {hasValidAmount && feePreview && !previewMatchesNet && (
                <Typography sx={{ fontSize: 12, color: t.form_label, mt: 1 }}>
                    Updating fee preview for the new amount…
                </Typography>
            )}
            <Elements
                stripe={stripePromise}
                options={options}
                key={`wallet-elements-${netForRequest}-${grossCents ?? 0}-${saveForFuture}`}>
                <WalletPaymentInner
                    amountCents={amountCents}
                    customPaymentChargeCents={customPaymentChargeCents}
                    netForRequest={netForRequest}
                    grossCents={grossCents}
                    feePreview={feePreview}
                    feeLoading={feeLoading}
                    previewMatchesNet={previewMatchesNet}
                    saveForFuture={saveForFuture}
                    onSaveForFutureChange={setSaveForFuture}
                    onPaymentComplete={onPaymentComplete}
                    paymentCurrency={paymentCurrency}
                    minAmountCents={minAmountCents}
                    fundOptions={fundOptions}
                    selectedPreset={selectedPreset}
                    activePreset={activePreset}
                />
            </Elements>
        </Box>
    );
}

/**
 * Inner component — has access to useStripe / useElements.
 * Creates intent via /campaign-wallet/fund with **net** wallet credit (cents), then confirms with Stripe.
 */
function WalletPaymentInner({
    amountCents,
    customPaymentChargeCents = null,
    netForRequest,
    grossCents,
    feePreview,
    feeLoading,
    previewMatchesNet,
    saveForFuture,
    onSaveForFutureChange,
    onPaymentComplete,
    paymentCurrency,
    minAmountCents,
    fundOptions,
    selectedPreset,
    activePreset,
}: {
    amountCents: number;
    customPaymentChargeCents?: number | null;
    netForRequest: number;
    grossCents: number | null;
    feePreview: ICampaignWalletSalesCalc | null;
    feeLoading: boolean;
    previewMatchesNet: boolean;
    saveForFuture: boolean;
    onSaveForFutureChange: (value: boolean) => void;
    onPaymentComplete: () => void;
    paymentCurrency: string;
    minAmountCents: number;
    fundOptions: IWalletFundOptions | null;
    selectedPreset: IWalletFundPreset | null;
    activePreset: IWalletFundPreset | null;
}) {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const stripe = useStripe();
    const elements = useElements();
    const dispatchPromise = useDispatchPromise();
    const dispatch = useDispatch();
    const { user } = getJWtDetails();

    const [paymentType, setPaymentType] = useState<IPType>();
    const [selectedCard, setSelectedCard] = useState<IPaymentCard | null>(null);
    const [isValidCard, setIsValidCard] = useState(false);
    const [processing, setProcessing] = useState(false);

    const hasValidAmount = isWalletTopUpAmountAllowed(
        amountCents,
        fundOptions,
        selectedPreset,
        customPaymentChargeCents,
    );
    const chargesInLocal = chargesInPaymentCurrency(fundOptions);
    const billingCountry = fundOptions?.country_code ?? 'US';
    const paymentReady = hasValidAmount && !feeLoading && previewMatchesNet && !!grossCents && grossCents >= 100;
    const stripePaymentAmount = paymentReady ? grossCents! : 0;

    const walletPreselectionMessage = !hasValidAmount
        ? getMinimumSelectPresetPrompt(fundOptions)
        : feeLoading
        ? 'Calculating fees…'
        : !grossCents || !previewMatchesNet
        ? 'Waiting for fee preview…'
        : getMinimumSelectPresetPrompt(fundOptions);

    const walletFundCreditCents = useMemo(
        () => resolveWalletCreditUsdCents(feePreview, netForRequest, selectedPreset),
        [feePreview, netForRequest, selectedPreset],
    );

    const requestWalletFundClientSecret = useCallback(async () => {
        if (!paymentReady) {
            throw new Error('Payment is not ready yet');
        }
        const fundResult = (await dispatchPromise(
            fundWallet(
                buildWalletFundStripeDispatchPayload({
                    walletCreditUsdCents: walletFundCreditCents,
                    saveCard: saveForFuture,
                    preset: selectedPreset,
                    activePreset,
                    fundOptions,
                    paymentCurrency,
                    userEmail: user?.email,
                    userName: user ? `${user.firstName} ${user.lastName}` : undefined,
                }),
            ),
        )) as IFundWalletResponse;

        if (!isStripeFundResponse(fundResult) || !fundResult.clientSecret) {
            showToastMessage('Failed to create payment intent. Please try again.', 'error');
            throw new Error('Failed to create payment intent');
        }
        return fundResult.clientSecret;
    }, [
        dispatchPromise,
        paymentReady,
        walletFundCreditCents,
        saveForFuture,
        selectedPreset,
        activePreset,
        fundOptions,
        paymentCurrency,
        user,
    ]);

    const resolveWalletClientSecret = useCallback(
        async (_params: ResolvePaymentClientSecretParams) => requestWalletFundClientSecret(),
        [requestWalletFundClientSecret],
    );

    const handleWalletPaymentSuccess = useCallback(() => {
        showToastMessage('Funds added successfully!', 'success');
        dispatch(getWalletDashboard());
        onPaymentComplete();
    }, [dispatch, onPaymentComplete]);

    const handleConfirm = async () => {
        if (!stripe || !hasValidAmount || feeLoading || !previewMatchesNet || !grossCents) return;

        if (paymentType === 'google_pay' || paymentType === 'apple_pay') {
            return;
        }

        if (paymentType === 'new_card') {
            if (!elements) return;
            const { error: submitError } = await elements.submit();
            if (submitError) {
                showToastMessage(submitError.message || 'Please check your card details.', 'error');
                return;
            }
        } else if (paymentType !== 'saved_card' || !selectedCard) {
            return;
        }

        setProcessing(true);

        try {
            const clientSecret = await requestWalletFundClientSecret();

            let confirmResult;

            if (paymentType === 'saved_card' && selectedCard) {
                confirmResult = await stripe.confirmPayment({
                    clientSecret,
                    confirmParams: {
                        payment_method: selectedCard.payment_method,
                        return_url: window.location.origin + window.location.pathname,
                    },
                    redirect: 'if_required',
                });
            } else if (paymentType === 'new_card' && elements) {
                confirmResult = await stripe.confirmPayment({
                    elements,
                    clientSecret,
                    confirmParams: {
                        return_url: window.location.origin + window.location.pathname,
                    },
                    redirect: 'if_required',
                });
            }

            if (confirmResult?.error) {
                showToastMessage(confirmResult.error.message || 'Payment failed', 'error');
            } else if (confirmResult?.paymentIntent?.status === 'succeeded') {
                handleWalletPaymentSuccess();
            } else {
                showToastMessage('Payment was not completed. Please try again.', 'error');
            }
        } catch {
            showToastMessage('Payment processing failed. Please try again.', 'error');
        } finally {
            setProcessing(false);
        }
    };

    const disableButton = () => {
        if (!stripe || !hasValidAmount || feeLoading || !previewMatchesNet || !grossCents) return true;
        if (paymentType === 'google_pay' || paymentType === 'apple_pay') return true;
        if (paymentType === 'new_card') return !isValidCard;
        if (paymentType === 'saved_card') return !selectedCard;
        return !paymentType;
    };

    const payLabel = activePreset
        ? `Pay ${formatYouPayDisplay(activePreset, fundOptions)}`
        : feePreview?.formatted?.total_amount && grossCents
        ? `Pay ${
              formatApiAmountLabel(feePreview.formatted.total_amount, fundOptions, activePreset) ??
              feePreview.formatted.total_amount
          }`
        : chargesInLocal
        ? `Pay ${formatChargeFromOptions(grossCents ?? stripePaymentAmount, fundOptions, activePreset)}`
        : `Pay ${formatUsdWalletCents(grossCents ?? stripePaymentAmount)}`;

    return (
        <>
            <Typography
                sx={{
                    fontSize: 11,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: t.form_label,
                    mb: 1.5,
                }}>
                Payment details
            </Typography>
            <PaymentMethodSelector
                key={`wallet-payment-${netForRequest}-${stripePaymentAmount}-${saveForFuture}`}
                saveForFuture={saveForFuture}
                onSaveForFutureChange={onSaveForFutureChange}
                digitalWalletsAlwaysVisible
                disablePaymentFields={!paymentReady}
                amount={stripePaymentAmount}
                preselectionRequired={!paymentReady}
                preselectionMessage={walletPreselectionMessage}
                onPaymentMethodSelected={type => setPaymentType(type)}
                onSelectSavedCard={card => setSelectedCard(card)}
                preselectCard
                onChange={change => setIsValidCard(change.complete)}
                resolvePaymentClientSecret={resolveWalletClientSecret}
                callBack={paymentIntent => {
                    if (isPaymentIntentResult(paymentIntent) && paymentIntent.status === 'succeeded') {
                        handleWalletPaymentSuccess();
                    }
                }}
                currency={paymentCurrency}
                details={{
                    email: user?.email || '',
                    name: user ? `${user.firstName} ${user.lastName}` : '',
                    saveCard: saveForFuture,
                    address: { line1: '', line2: '', city: '', state: '', postal_code: '', country: billingCountry },
                }}
                metadata={{
                    category: 'campaign_wallet_funding',
                    metadata: {
                        purpose: 'campaign_wallet_funding',
                        email: user?.email,
                        name: user ? `${user.firstName} ${user.lastName}` : undefined,
                    },
                }}
            />
            <Button
                fullWidth
                onClick={handleConfirm}
                disabled={!!disableButton() || processing}
                sx={{
                    mt: 3,
                    py: 2,
                    borderRadius: 999,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 17,
                    textTransform: 'none',
                    color: t.pure_black,
                    bgcolor: t.primary_orange,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: t.primary_orange },
                    ':disabled': { bgcolor: t.deep_grey_lighter, color: t.form_label },
                }}>
                {processing && <CircularProgress size={22} sx={{ color: t.pure_black, mr: 1 }} />}
                {processing ? 'Processing…' : hasValidAmount ? payLabel : getMinimumEnterAmountPrompt(fundOptions)}
            </Button>
            <Typography
                sx={{
                    textAlign: 'center',
                    fontSize: 10,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: t.form_label,
                    mt: 1.5,
                }}>
                Secure 256-bit SSL encrypted payment
            </Typography>
        </>
    );
}
